import { Component, ElementRef, HostListener, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewChild, computed, signal, input, output } from '@angular/core'
import { form } from '@angular/forms/signals'
import { PageEvent } from '@angular/material/paginator'
import { LaskuAsiakkaatHakuPyynto, LaskuAsiakkaatHakuVastaus, LaskunAsiakas, LaskunAsiakastyyppi, LaskunLahetystapa, LaskunTyyppiavain } from '@lemon/core-firebase/model/lasku'
import { EMPTY, Subject, from } from 'rxjs'
import { debounceTime, map, switchMap, takeUntil } from 'rxjs/operators'
import { FirebaseLemonaid } from '../../../_angular/service/firebase-lemonaid.service'
import { arvonMuutokset, asetaArvo } from '../../../_jaettu-angular/_lomakkeet/arvon-muutokset'
import { LaskutusHakuStateService } from '../../../_jaettu-angular/laskut/haku/laskutus-haku-state.service'
import { KonfiguraatioPalvelu } from '../../../_jaettu-angular/service/lasku/configuration.service'
import { AsiakasDialogiService } from '../asiakas-dialogi.service'

type AsiakasHakuPaneeli = 'tyyppi' | 'myynninLaji' | 'toimitustapa' | 'paikkakunta'

interface AsiakastyyppiVaihtoehto {
  tyyppi: LaskunAsiakastyyppi
  nimiAvain: string
}

interface ToimitustapaVaihtoehto {
  lahetystapa: LaskunLahetystapa
  nimiAvain: string
}

interface NaytettavaAsiakas extends LaskunAsiakas {
  toimitusteksti: string
}

@Component({
  selector: 'app-asiakasrekisteri-haku',
  templateUrl: './asiakasrekisteri-haku.component.html',
  styleUrls: ['./asiakasrekisteri-haku.component.css'],
  standalone: false,
})
export class AsiakasrekisteriHakuComponent implements OnInit, OnChanges, OnDestroy {
  readonly avoinna = input(false)
  readonly suljettu = output<void>()

  @ViewChild('hakukentta', { read: ElementRef }) set hakukentta(kentta: ElementRef<HTMLInputElement> | undefined) {
    if (kentta) {
      setTimeout(() => kentta.nativeElement.focus())
    }
  }

  hakuteksti = form(signal(''))
  readonly hakusana = computed(() => this.hakuteksti().value())
  readonly tulokset = signal<NaytettavaAsiakas[]>([])
  readonly haetaan = signal(false)
  readonly kokonaismaara = signal(0)
  sivu = 0
  sivunKoko = 50
  readonly sivukoot = [50, 100, 200]

  private _hakuTrigger = new Subject<void>()
  private _edellinenHakuJson: string | null = null
  private _ngUnsubscribe = new Subject<void>()

  avoinPaneeli: AsiakasHakuPaneeli | null = null

  readonly kaikkiTyypit: AsiakastyyppiVaihtoehto[] = [
    { tyyppi: LaskunAsiakastyyppi.YRITYS, nimiAvain: 'asiakkaat.haku.tyyppi-yritys' },
    { tyyppi: LaskunAsiakastyyppi.HENKILO, nimiAvain: 'asiakkaat.haku.tyyppi-henkilo' },
  ]
  valitutTyypit = new Set<LaskunAsiakastyyppi>()

  readonly kaikkiMyynninLajit = signal<LaskunTyyppiavain[]>([])
  valitutMyynninLajit = new Set<LaskunTyyppiavain>()

  readonly kaikkiToimitustavat: ToimitustapaVaihtoehto[] = [
    { lahetystapa: LaskunLahetystapa.SAHKOINEN, nimiAvain: 'lasku.asiakas-dialog.toimitustapa.verkkolasku' },
    { lahetystapa: LaskunLahetystapa.SAHKOPOSTI, nimiAvain: 'lasku.asiakas-dialog.toimitustapa.sahkoposti' },
    { lahetystapa: LaskunLahetystapa.ITSE, nimiAvain: 'lasku.asiakas-dialog.toimitustapa.itse' },
  ]
  valitutToimitustavat = new Set<LaskunLahetystapa>()

  readonly kaikkiPaikkakunnat = signal<string[]>([])
  valitutPaikkakunnat = new Set<string>()
  paikkakuntaRajaus = form(signal(''))

  constructor(
    private _firebaseLemonaid: FirebaseLemonaid,
    private _hakuStateService: LaskutusHakuStateService,
    private _asiakasDialogiService: AsiakasDialogiService,
    private _konfiguraatioPalvelu: KonfiguraatioPalvelu,
  ) {
    arvonMuutokset(this.hakuteksti).subscribe(() => this.hakutekstiMuuttui())
  }

  ngOnInit() {
    this._konfiguraatioPalvelu.tyypitObservable.pipe(takeUntil(this._ngUnsubscribe)).subscribe(tyypit => {
      this.kaikkiMyynninLajit.set((tyypit ?? []).map(tyyppi => tyyppi.avain))
    })

    this._hakuTrigger
      .pipe(
        debounceTime(500),
        switchMap(() => {
          const hakuehdotPuuttuvat = !this.onkoHakuehtoja
          if (hakuehdotPuuttuvat) {
            this.haetaan.set(false)
            this.tulokset.set([])
            this.kokonaismaara.set(0)
            this._edellinenHakuJson = null
            return EMPTY
          }
          const pyynto = this._rakennaHakupyynto()
          const pyyntoJson = JSON.stringify(pyynto)
          if (pyyntoJson === this._edellinenHakuJson) {
            this.haetaan.set(false)
            return EMPTY
          }
          return from(this._suoritaHaku(pyynto)).pipe(map(vastaus => ({ vastaus, pyyntoJson })))
        }),
        takeUntil(this._ngUnsubscribe),
      )
      .subscribe(({ vastaus, pyyntoJson }) => this._kasitteleHakuvastaus(vastaus, pyyntoJson))

    const tallennettuHakutila = this._hakuStateService.asiakas.palautaHakutila()
    if (tallennettuHakutila) {
      asetaArvo(this.hakuteksti, tallennettuHakutila.hakuteksti, { ilmoita: false })
      this.sivu = tallennettuHakutila.sivu
      this.sivunKoko = tallennettuHakutila.sivunKoko
      this._hakuTrigger.next()
    }
  }

  ngOnDestroy() {
    this._ngUnsubscribe.next()
    this._ngUnsubscribe.complete()
  }

  ngOnChanges(changes: SimpleChanges) {
    if (!changes.avoinna) {
      return
    }
    if (changes.avoinna.currentValue) {
      this._hakuTrigger.next()
    } else {
      this.tulokset.set([])
      this.kokonaismaara.set(0)
      // Nollaus varmistaa, että uudelleen avattaessa haetaan tuoreet tulokset samoillakin ehdoilla
      this._edellinenHakuJson = null
    }
  }

  @HostListener('window:keydown.escape')
  suljeEscapella() {
    if (!this.avoinna()) {
      return
    }
    if (this.avoinPaneeli) {
      this.avoinPaneeli = null
    } else {
      this.sulje()
    }
  }

  @HostListener('document:click', ['$event'])
  dokumenttiaKlikattu(event: MouseEvent) {
    const target = event.target as HTMLElement | null
    const klikkausSuodattimessa = !!target?.closest('.dropdown-wrap')
    if (this.avoinPaneeli && !klikkausSuodattimessa) {
      this.avoinPaneeli = null
    }
  }

  get onkoHakuehtoja(): boolean {
    return !!this.hakuteksti().value().trim() || this.valitutTyypit.size > 0 || this.valitutMyynninLajit.size > 0 || this.valitutToimitustavat.size > 0 || this.valitutPaikkakunnat.size > 0
  }

  get rajatutPaikkakunnat(): string[] {
    const rajaus = this.paikkakuntaRajaus().value().trim().toLowerCase()
    if (!rajaus) {
      return this.kaikkiPaikkakunnat()
    }
    return this.kaikkiPaikkakunnat().filter(paikkakunta => paikkakunta.toLowerCase().includes(rajaus))
  }

  vaihdaPaneeli(paneeli: AsiakasHakuPaneeli) {
    this.avoinPaneeli = this.avoinPaneeli === paneeli ? null : paneeli
    const paikkakunnatLataamatta = this.avoinPaneeli === 'paikkakunta' && this.kaikkiPaikkakunnat().length === 0
    if (paikkakunnatLataamatta) {
      this._lataaPaikkakunnat()
    }
  }

  vaihdaTyyppi(tyyppi: LaskunAsiakastyyppi) {
    if (this.valitutTyypit.has(tyyppi)) {
      this.valitutTyypit.delete(tyyppi)
    } else {
      this.valitutTyypit.add(tyyppi)
    }
    this.hakutekstiMuuttui()
  }

  tyhjennaTyypit() {
    this.valitutTyypit.clear()
    this.hakutekstiMuuttui()
  }

  vaihdaMyynninLaji(myynninLaji: LaskunTyyppiavain) {
    if (this.valitutMyynninLajit.has(myynninLaji)) {
      this.valitutMyynninLajit.delete(myynninLaji)
    } else {
      this.valitutMyynninLajit.add(myynninLaji)
    }
    this.hakutekstiMuuttui()
  }

  tyhjennaMyynninLajit() {
    this.valitutMyynninLajit.clear()
    this.hakutekstiMuuttui()
  }

  vaihdaToimitustapa(lahetystapa: LaskunLahetystapa) {
    if (this.valitutToimitustavat.has(lahetystapa)) {
      this.valitutToimitustavat.delete(lahetystapa)
    } else {
      this.valitutToimitustavat.add(lahetystapa)
    }
    this.hakutekstiMuuttui()
  }

  tyhjennaToimitustavat() {
    this.valitutToimitustavat.clear()
    this.hakutekstiMuuttui()
  }

  vaihdaPaikkakunta(paikkakunta: string) {
    if (this.valitutPaikkakunnat.has(paikkakunta)) {
      this.valitutPaikkakunnat.delete(paikkakunta)
    } else {
      this.valitutPaikkakunnat.add(paikkakunta)
    }
    this.hakutekstiMuuttui()
  }

  tyhjennaPaikkakunnat() {
    this.valitutPaikkakunnat.clear()
    this.hakutekstiMuuttui()
  }

  tyhjennaHakuteksti() {
    asetaArvo(this.hakuteksti, '', { ilmoita: false })
    this.hakutekstiMuuttui()
  }

  tyhjennaKaikki() {
    this.valitutTyypit.clear()
    this.valitutMyynninLajit.clear()
    this.valitutToimitustavat.clear()
    this.valitutPaikkakunnat.clear()
    this.tyhjennaHakuteksti()
  }

  sulje() {
    this.suljettu.emit()
  }

  taustaaKlikattu(event: MouseEvent) {
    if (event.target === event.currentTarget) {
      this.sulje()
    }
  }

  hakutekstiMuuttui() {
    this.sivu = 0
    this._hakuTrigger.next()
  }

  sivuMuuttui(event: PageEvent) {
    this.sivu = event.pageIndex
    this.sivunKoko = event.pageSize
    this._hakuTrigger.next()
  }

  avaaAsiakas(asiakas: LaskunAsiakas) {
    this._asiakasDialogiService.avaaAsiakasrekisterinAsiakas(asiakas)
  }

  private async _suoritaHaku(pyynto: LaskuAsiakkaatHakuPyynto): Promise<LaskuAsiakkaatHakuVastaus | null> {
    this.haetaan.set(true)
    try {
      return await this._firebaseLemonaid.functionsCall<LaskuAsiakkaatHakuPyynto, LaskuAsiakkaatHakuVastaus>('laskutEtsiAsiakas', pyynto, undefined, 'europe-north1')
    } catch {
      return null
    }
  }

  private async _lataaPaikkakunnat() {
    const pyynto: LaskuAsiakkaatHakuPyynto = { ylaraja: 1, sivu: 0 }
    const vastaus = await this._firebaseLemonaid.functionsCall<LaskuAsiakkaatHakuPyynto, LaskuAsiakkaatHakuVastaus>('laskutEtsiAsiakas', pyynto, undefined, 'europe-north1').catch(() => null)
    this.kaikkiPaikkakunnat.set(vastaus?.paikkakunnat ?? [])
  }

  private _kasitteleHakuvastaus(vastaus: LaskuAsiakkaatHakuVastaus | null, pyyntoJson: string) {
    this.haetaan.set(false)
    if (!vastaus || vastaus.e) {
      this.tulokset.set([])
      this.kokonaismaara.set(0)
      this._edellinenHakuJson = null
      return
    }
    this._edellinenHakuJson = pyyntoJson
    this.tulokset.set((vastaus.asiakkaat ?? []).map(asiakas => ({ ...asiakas, toimitusteksti: this._asiakasDialogiService.annaToimitusteksti(asiakas) })))
    this.kokonaismaara.set(vastaus.kokonaismaara ?? 0)
    this.kaikkiPaikkakunnat.set(vastaus.paikkakunnat ?? [])
  }

  private _rakennaHakupyynto(): LaskuAsiakkaatHakuPyynto {
    const pyynto: LaskuAsiakkaatHakuPyynto = { ylaraja: this.sivunKoko, sivu: this.sivu }
    const teksti = this.hakuteksti().value().trim()
    if (teksti) {
      pyynto.vapaahaku = teksti
    }
    if (this.valitutTyypit.size > 0) {
      pyynto.asiakastyypit = Array.from(this.valitutTyypit)
    }
    if (this.valitutMyynninLajit.size > 0) {
      pyynto.laskunTyypit = Array.from(this.valitutMyynninLajit)
    }
    if (this.valitutToimitustavat.size > 0) {
      pyynto.lahetystavat = Array.from(this.valitutToimitustavat)
    }
    if (this.valitutPaikkakunnat.size > 0) {
      pyynto.paikkakunnat = Array.from(this.valitutPaikkakunnat)
    }
    return pyynto
  }
}
